import { z } from 'zod';
import { timestampSchema, uuidSchema } from './common';
import { entityDefinition, isEntityKind } from './entities';
import type { Permission } from './permissions';

/**
 * Stored files: an image embedded in a Markdown body today, any attachment
 * tomorrow (docs/rich-text-design.md sections 5-7, revisited for S3-only
 * storage and a general attachment shape in docs/decisions.md).
 *
 * An asset is uploaded before it has anywhere to belong -- a field is usually
 * edited before the record around it is ever saved -- so it starts with no
 * anchor and is *claimed* by whichever save first references it. Reading one
 * is gated by its anchor's own permission once it has one, and by the
 * uploader alone before that. None of this is enforced here; this file only
 * declares the shapes and the pure functions every layer shares, so the rule
 * is written once.
 */

/* ---------------------------------------------------------------- anchors */

/**
 * What an asset may be attached to. Wider than `EntityKind`: a meeting's
 * purpose text and an agenda item's body both take Markdown and both want
 * images, but `meeting` and `meeting_agenda_item` are deliberately not entity
 * kinds (docs/meetings-design.md section 15) -- there is no `EntityKind`
 * union member to reuse. This union exists only for `Asset`; every other
 * entity-keyed mechanism (`entity_link`, `entity_revision`, `entity_comment`,
 * `entity_vote`) keeps using `EntityKind` unchanged.
 */
export const ASSET_ANCHOR_KINDS = [
  'schedule_task',
  'map_feature',
  'infrastructure_asset',
  'cost_account',
  'cost_entry',
  'risk',
  'opportunity',
  'idea',
  'decision',
  'issue',
  /* A photograph is frequently the whole of a safety observation, so this is
     the anchor kind that most needed to exist. */
  'observation',
  /* A revision's file is the whole point of the revision, so this is the
     anchor kind the document register is built on. */
  'document_revision',
  /* The document itself, which is a different anchor from the one above and
     both are needed. A *revision* anchors the drawing it is an issue of, named
     in a column; a *document* anchors whatever somebody pastes into a comment
     about it -- a photograph of the clash, a marked-up extract. Every
     `EntityKind` has to appear here for that second reason, which is why this
     list is the wider of the two. */
  'document',
  'meeting',
  'meeting_agenda_item',
  /* The fleet. A registration certificate or a certificate of conformity is
     the evidence a declaration is verified against, and it hangs off the
     vehicle; the agreement anchors what somebody pastes into a comment about
     it. Both are `EntityKind`s, so both have to be here regardless. */
  'vehicle',
  'framework_agreement',
] as const;
export const assetAnchorKindSchema = z.enum(ASSET_ANCHOR_KINDS);
export type AssetAnchorKind = (typeof ASSET_ANCHOR_KINDS)[number];

/**
 * The permission that gates reading an asset anchored at this kind.
 *
 * Delegates to `entityDefinition` for the kinds that are also `EntityKind`s,
 * so a change to one of those permissions is not a second place to update.
 * The remaining kinds have no `LinkEntityProvider` to resolve against, so
 * their permission is named directly rather than looked up:
 *
 *  - The two meeting kinds -- by design, `meeting` cannot be linked or
 *    discussed either.
 *  - `cost_entry` -- deliberately *not* `cost_account`'s own `cost.read`.
 *    An uploaded document (an invoice, say) is exactly the kind of
 *    commercially confidential material `cost.read_detail` exists to gate;
 *    anchoring it at the coarser account-level permission would let anyone
 *    who can see the aggregate KPI curve also fetch the raw file.
 */
export function assetAnchorReadPermission(kind: AssetAnchorKind): Permission {
  if (isEntityKind(kind)) return entityDefinition(kind).readPermission;
  if (kind === 'cost_entry') return 'cost.read_detail';
  if (kind === 'document_revision') return 'document.read';
  return 'meeting.read';
}

/** The anchor as a query pair, mirroring `entityAnchorQuerySchema`. */
export const assetAnchorQuerySchema = z.object({
  kind: assetAnchorKindSchema,
  id: uuidSchema,
});
export type AssetAnchor = z.infer<typeof assetAnchorQuerySchema>;

/* ------------------------------------------------------------------ kinds */

export const ASSET_KINDS = ['image', 'file'] as const;
export const assetKindSchema = z.enum(ASSET_KINDS);
export type AssetKind = (typeof ASSET_KINDS)[number];

/** What `purpose` an upload declares, and what it is validated against.
 *  Same values as `AssetKind` -- kept as a separate export because one names
 *  a request and the other names a stored fact, even though today they agree
 *  exactly. */
export const uploadAssetPurposeSchema = assetKindSchema;
export type UploadAssetPurpose = z.infer<typeof uploadAssetPurposeSchema>;

/**
 * Content types this platform will store, by purpose. Free text on the
 * `Asset` row rather than a Postgres enum, so a new accepted type is a change
 * here, not a migration. SVG is deliberately absent from the image list: it
 * is XML and can carry a script, which is the exact stored-XSS class the
 * Markdown renderer exists to close (docs/rich-text-design.md section 5.4).
 */
export const ALLOWED_IMAGE_CONTENT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
] as const;

/**
 * A conservative starting allowlist for non-image attachments. Deliberately
 * short: each addition is a real question ("can this be made provably inert
 * the way an image is re-encoded to be") rather than a blanket allowance.
 */
export const ALLOWED_FILE_CONTENT_TYPES = ['application/pdf'] as const;

export function allowedContentTypesFor(purpose: UploadAssetPurpose): readonly string[] {
  return purpose === 'image' ? ALLOWED_IMAGE_CONTENT_TYPES : ALLOWED_FILE_CONTENT_TYPES;
}

/** Post-processing cap: an image is re-encoded and resized before storage, so
 *  this is the size of what this platform actually writes, not of whatever a
 *  camera produced. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
/** No re-encode step exists for a non-image file, so this is the upload cap
 *  as received. */
export const MAX_FILE_BYTES = 20 * 1024 * 1024;

export function maxBytesFor(purpose: UploadAssetPurpose): number {
  return purpose === 'image' ? MAX_IMAGE_BYTES : MAX_FILE_BYTES;
}

/** The longest edge an image is resized to, in pixels. */
export const MAX_IMAGE_DIMENSION = 2000;

/* -------------------------------------------------------------------- wire */

export const assetMetadataSchema = z
  .object({
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
  })
  .strict();
export type AssetMetadata = z.infer<typeof assetMetadataSchema>;

export const assetSchema = z.object({
  id: uuidSchema,
  entityKind: assetAnchorKindSchema.nullable(),
  entityId: uuidSchema.nullable(),
  uploadedByUserId: uuidSchema.nullable(),
  kind: assetKindSchema,
  contentType: z.string(),
  byteSize: z.number().int().nonnegative(),
  filename: z.string().nullable(),
  metadata: assetMetadataSchema,
  createdAt: timestampSchema,
});
export type Asset = z.infer<typeof assetSchema>;

/** Query string for `POST /api/v1/assets`. The bytes are the raw request
 *  body; everything else about the upload travels here. */
export const uploadAssetQuerySchema = z
  .object({
    purpose: uploadAssetPurposeSchema,
    /** Sanitized and truncated server-side; never trusted beyond a default
     *  alt text and a download name. */
    filename: z.string().max(255).optional(),
  })
  .strict();
export type UploadAssetQuery = z.infer<typeof uploadAssetQuerySchema>;

/**
 * Why this file cannot be uploaded for this purpose, or null if it can.
 *
 * The client-side half of a check the server makes properly -- `AssetsService`
 * sniffs the content rather than trusting a declared `Content-Type`, and this
 * cannot and does not try to. Its whole job is fast feedback: telling somebody
 * their 40 MB scan is too large before they wait for it to upload.
 *
 * Stated here because three controls ask it and each had been pairing an
 * `ALLOWED_*_CONTENT_TYPES` with a `MAX_*_BYTES` by hand, even though
 * `allowedContentTypesFor` and `maxBytesFor` have derived both from the purpose
 * since this file was written. Hand-pairing them is how a control comes to
 * accept a file the upload then refuses.
 *
 * Returns the reason rather than a message: the wording belongs to the caller,
 * whose namespace it lives in, and the rule does not.
 *
 * Takes the two properties it reads rather than a `File`, so this stays usable
 * from anywhere -- a contract shared with the API and the worker has no DOM.
 */
export type AssetFileRejection = 'type' | 'size';

export function assetFileRejection(
  file: { type: string; size: number },
  purpose: UploadAssetPurpose,
): AssetFileRejection | null {
  if (!allowedContentTypesFor(purpose).includes(file.type)) return 'type';
  if (file.size > maxBytesFor(purpose)) return 'size';
  return null;
}

/* --------------------------------------------------------------- addressing */

/**
 * Where an asset is served from.
 *
 * The write half of the same fact `extractAssetIds` reads. That function's own
 * comment argues for one regex in one place "so the image rule, a future
 * file-link rule and the claim step agree by construction rather than by three
 * implementations staying in sync" -- and the URL it matches was meanwhile
 * being built by hand in six of them, in the web app and in this package,
 * including inside `revisionSource`.
 *
 * The asymmetry mattered more than it looks. The sweep that decides an asset is
 * unreferenced re-reads columns and prose with `extractAssetIds`; anything
 * writing this path in a subtly different shape would be storing a reference
 * that function cannot find, and the consequence of an unfindable reference is
 * a deleted file. So both halves are stated here, next to each other, and the
 * pattern below is the one the extractor matches.
 */
export function assetHref(assetId: string): string {
  return `/api/v1/assets/${assetId}`;
}

/* -------------------------------------------------------------- extraction */

/**
 * Every asset id referenced anywhere in a piece of Markdown, deduplicated.
 *
 * A pure function over a string, not a parse of the Markdown grammar: an
 * asset reference is `/api/v1/assets/<uuid>` wherever it appears, whether
 * inside `![alt](...)` (an embedded image), inside `[label](...)` (a future
 * file-download link), or bare. One regex, one place, so the image rule, a
 * future file-link rule and the claim step agree by construction rather than
 * by three implementations staying in sync.
 *
 * This is also why claiming is not "parse the Markdown and find image
 * nodes": `docs/meetings-design.md` section 3.1 already rejected recovering
 * structure from prose for the join that matters (which items reference
 * which); this is not that. It is a much smaller question -- "which ids does
 * this string mention" -- asked only so an upload can be attached to
 * whatever saved it, never to answer "what references this item".
 */
export function extractAssetIds(text: string | null | undefined): string[] {
  if (!text) return [];
  const pattern =
    /\/api\/v1\/assets\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;
  const found = new Set<string>();
  for (const match of text.matchAll(pattern)) {
    found.add(match[1]!.toLowerCase());
  }
  return [...found];
}
